import { HexColor } from '@nx-smartmonkey/shared/domain';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { analyticsService } from '../../../application/AnalyticsService';
import { DateChartAnalytics } from '../../../domain/analytics/DateChartAnalytics';
import { Subscription } from '../../../domain/subscription/Subscription';
import { VehicleFlat } from '../../../domain/vehicle/VehicleFlat';
import { intl } from '../../../helpers/IntGlobalProvider';
import { BinTypes } from '../../../interfaces/analytics';
import { hasAnalyticsPrivileges } from '../../../pages/analytics/shared/AnalyticsPermissions';
import { formatDateByBinType } from '../../../pages/analytics/shared/ChartHeaderFilters';
import useErrorMiddleware from '../../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../../project/useRetrieveActiveProject';

import { useRetrieveUser } from '../../user/useRetrieveUser';

interface CustomersSatisfactionProps {
  date: string;
  dateLabel: string;
  total: number;
}

type UseRetrieveAnalyticsSatisfactionResponse = ServiceResponse<null, CustomersSatisfactionProps[]> & {
  data: CustomersSatisfactionProps[];
};

const getVehiclesAll = () =>
  VehicleFlat.create(
    {
      label: intl.formatMessage({ id: `project_analytics.vehicles_all` }),
      color: HexColor.create(`#393939`),
    },
    `all_vehicles`
  );

// Hooks act as 'Adapter' layer.
export const useRetrieveAnalyticsSatisfaction = ({
  startDate,
  endDate,
  binType,
  vehicles,
  subscription,
}: {
  startDate: Date;
  endDate: Date;
  binType: BinTypes; // TODO: REMOVE FROM INTERFACES
  vehicles: Array<VehicleFlat>;
  subscription: Subscription;
}): UseRetrieveAnalyticsSatisfactionResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: user } = useRetrieveUser();

  const [isLoading, setIsLoading] = useState(true);
  const [satisfactionResponse, setSatisfactionResponse] = useState<DateChartAnalytics[][] | undefined>();
  const [satisfactionData, setSatisfactionData] = useState<CustomersSatisfactionProps[] | undefined>();

  useQuery(
    [`customers_analytics_satisfaction`, startDate, endDate, binType, vehicles],
    () =>
      Promise.all(
        (vehicles.length > 0 ? vehicles : [getVehiclesAll()]).map((vehicle) =>
          analyticsService.getAnalyticsCustomersSatisfaction({
            startDate,
            endDate,
            binType,
            ...(vehicle.id !== getVehiclesAll().id ? { vehicleId: vehicle.id } : {}),
            projectId: activeProject!.id,
          })
        )
      ),
    {
      enabled:
        !!user &&
        !!activeProject &&
        hasAnalyticsPrivileges({
          planType: subscription.plan,
        }),
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (response: DateChartAnalytics[][]) => {
        setSatisfactionResponse(response);
      },
      onError: (err: any) => {
        console.info(`Customers analytics satisfaction on route error`, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
      },
    }
  );

  useEffect(() => {
    if (satisfactionResponse) {
      setIsLoading(false);

      const locale = user!.language.value;

      const vehiclesData = vehicles.length > 0 ? vehicles : [getVehiclesAll()];

      const newData: CustomersSatisfactionProps[] = [];

      if (vehiclesData.length === 0) {
        setSatisfactionData(newData);
      }

      satisfactionResponse[0].forEach((firstResponse) => {
        const dateLabel = formatDateByBinType(firstResponse.date, binType, locale);
        const object = {
          date: firstResponse.date,
          dateLabel,
          [vehiclesData[0].id]: firstResponse.value,
        } as Record<string, any>;

        satisfactionResponse.forEach((_p, index) => {
          if (index > 0) {
            const vehicleValue = (satisfactionResponse[index].find((i) => i.date === firstResponse.date) ?? {}).value;
            object[vehiclesData[index].id] = vehicleValue;
          }
        });

        newData.push(object as CustomersSatisfactionProps);
      });

      setSatisfactionData(newData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [satisfactionResponse]);

  return {
    error: false,
    isLoading: isLoading,
    success: false,
    run: null,
    data: satisfactionData || [],
  };
};

export default useRetrieveAnalyticsSatisfaction;
